import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  HeartIcon,
  PhoneIcon,
  MapPinIcon,
  ClockIcon,
  ArrowLeftIcon,
  UserGroupIcon,
  BeakerIcon,
  TruckIcon
} from '@heroicons/react/24/outline';
import { useContactInfo, useBranding } from '../hooks/useHospitalConfig';

interface ServiceInfo {
  id: string;
  name: string;
  description: string;
  icon: React.ComponentType<any>;
  hours: string;
  contact: string;
  location: string;
}


export const ServiceInformationPage: React.FC = () => {
  const navigate = useNavigate();
  const { emergency, general, loading: contactLoading } = useContactInfo();
  const { hospitalName } = useBranding();

  const services: ServiceInfo[] = [
    {
      id: 'emergency',
      name: 'Emergency Services',
      description: '24/7 emergency care with state-of-the-art facilities and expert medical staff for critical and urgent medical needs.',
      icon: HeartIcon,
      hours: '24/7',
      contact: emergency?.phone_display || '(555) 911-CARE',
      location: 'Emergency Department - Main Building'
    },
    {
      id: 'outpatient',
      name: 'Outpatient Services',
      description: 'Comprehensive outpatient care including consultations, minor procedures, and follow-up appointments.',
      icon: UserGroupIcon,
      hours: 'Mon-Fri: 8AM-6PM, Sat: 9AM-2PM',
      contact: general?.phone_display || '(555) 123-4567',
      location: 'Outpatient Wing - 2nd Floor'
    },
    {
      id: 'diagnostics',
      name: 'Diagnostic Services',
      description: 'Complete diagnostic services including laboratory tests, imaging (X-ray, MRI, CT), and specialized testing.',
      icon: BeakerIcon,
      hours: 'Mon-Fri: 7AM-7PM, Sat: 8AM-4PM',
      contact: general?.phone_display || '(555) 123-4568',
      location: 'Diagnostic Center - Ground Floor'
    },
    {
      id: 'pharmacy',
      name: 'Hospital Pharmacy',
      description: 'Full-service pharmacy providing prescription medications, over-the-counter drugs, and medication counseling.',
      icon: TruckIcon,
      hours: 'Mon-Fri: 8AM-8PM, Sat-Sun: 9AM-5PM',
      contact: general?.phone_display || '(555) 123-4569',
      location: 'Pharmacy - Main Lobby'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <button 
              onClick={() => navigate('/onecare/')}
              className="flex items-center group"
            >
              <div className="flex-shrink-0">
                <div className="h-12 w-12 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-lg flex items-center justify-center">
                  <HeartIcon className="h-8 w-8 text-white" />
                </div>
              </div>
              <div className="ml-4">
                <h1 className="text-2xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                  {hospitalName || 'Aimepic OneCare'}
                </h1>
                <p className="text-sm text-gray-800">Hospital Services</p>
              </div>
            </button>
            
            <nav className="flex space-x-8">
              <button 
                onClick={() => navigate('/onecare/patients')}
                className="flex items-center text-gray-900 hover:text-blue-600 transition-colors"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                Back to Options
              </button>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-extrabold text-gray-900 mb-4">
            Hospital Services
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-600">
              Complete Healthcare Solutions
            </span>
          </h1>
          <p className="text-xl text-gray-900 max-w-3xl mx-auto">
            Learn about our comprehensive medical services, facilities, and how to access the care you need.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid gap-8 mb-12">
          {services.map((service) => {
            const IconComponent = service.icon;
            
            return (
              <div
                key={service.id}
                className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100 hover:shadow-xl transition-shadow duration-300"
              >
                <div className="flex items-start space-x-6">
                  <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center shadow-lg">
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                      {service.name}
                    </h3>
                    <p className="text-gray-900 text-base leading-relaxed mb-6">
                      {service.description}
                    </p>
                    
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="flex items-center space-x-3">
                        <ClockIcon className="h-5 w-5 text-blue-600" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Hours</p>
                          <p className="text-sm text-gray-900">{service.hours}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-3">
                        <PhoneIcon className="h-5 w-5 text-blue-600" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Contact</p>
                          <p className="text-sm text-gray-900">{service.contact}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-3">
                        <MapPinIcon className="h-5 w-5 text-blue-600" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Location</p>
                          <p className="text-sm text-gray-900">{service.location}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Additional Information */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">General Hospital Information</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <PhoneIcon className="h-5 w-5 text-blue-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Main Hospital</p>
                    <p className="text-sm text-gray-900">{general?.phone_display || '(555) 123-4567'}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <PhoneIcon className="h-5 w-5 text-red-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Emergency</p>
                    <p className="text-sm text-gray-900">{emergency?.phone_display || '(555) 911-CARE'}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <MapPinIcon className="h-5 w-5 text-blue-600 mt-1" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Address</p>
                    <p className="text-sm text-gray-900">123 Healthcare Boulevard<br />Medical City, MC 12345</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Important Resources</h3>
              <div className="space-y-3">
                <div>
                  <h4 className="text-sm font-medium text-gray-900">Patient Rights & Responsibilities</h4>
                  <p className="text-sm text-gray-900">Information about your rights as a patient and our commitment to quality care.</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-900">Visiting Hours</h4>
                  <p className="text-sm text-gray-900">General visiting: 9AM-8PM daily. ICU visiting hours may vary.</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-900">Parking Information</h4>
                  <p className="text-sm text-gray-900">Free patient and visitor parking available. Valet services offered.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">
            Need to communicate with hospital staff about your care?
          </h3>
          <button
            onClick={() => navigate('/onecare/patients/encounter')}
            className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white font-semibold py-3 px-8 rounded-lg transition-all duration-300 transform hover:-translate-y-0.5 shadow-lg hover:shadow-xl"
          >
            Go to Patient Communication Portal
          </button>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-20">
        <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center text-sm text-gray-800">
              <HeartIcon className="h-4 w-4 mr-2" />
              <span>HIPAA-Ready & Secure</span>
            </div>
            
            <div className="flex items-center space-x-6 text-sm text-gray-800">
              <button
                onClick={() => window.open('/patient/privacy', '_blank')}
                className="hover:text-blue-600 transition-colors"
              >
                Privacy Policy
              </button>
              <button
                onClick={() => window.open('/patient/help', '_blank')}
                className="hover:text-blue-600 transition-colors"
              >
                Help & Support
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ServiceInformationPage;